import { ref, computed } from "vue";
import api from "@/api/client";

export interface EventConfigData {
  lottery_roll_ms: number;
  lottery_reveal_ms: number;
  lottery_flip_ms: number;
  line_friend_url: string | null;
  event_start_at: string | null;
  event_end_at: string | null;
  points_per_lottery: number;
  lottery_enabled: boolean;
  redemption_enabled: boolean;
  redemption_tiers: number[];
  lottery_tiers: number[];
  max_units_per_qr: number;
  max_units_per_staff_entry: number;
  unit_label: string;
  status: string;
  is_live: boolean;
}

// Offline/degraded fallback only — used if /api/event-config is unreachable.
// These mirror the production defaults; the live values always come from the
// API (the backend EventConfig singleton is the source of truth).
const DEFAULTS: EventConfigData = {
  lottery_roll_ms: 1500,
  lottery_reveal_ms: 2000,
  lottery_flip_ms: 200,
  line_friend_url: null,
  event_start_at: null,
  event_end_at: null,
  points_per_lottery: 6,
  lottery_enabled: true,
  redemption_enabled: true,
  redemption_tiers: [6, 12, 18, 24],
  lottery_tiers: [6, 12, 18, 24],
  max_units_per_qr: 3,
  max_units_per_staff_entry: 99,
  unit_label: "杯",
  // Default to live so the gate never flashes before /event-config returns.
  status: "active",
  is_live: true,
};

// Module-level singleton so every consumer shares one reactive copy and we
// fetch /event-config exactly once per app load (mirrors useEventTitle).
const config = ref<EventConfigData>({ ...DEFAULTS });
let loaded = false;

async function loadEventConfig() {
  if (loaded) return;
  loaded = true;
  try {
    const r = await api.get<EventConfigData>("/event-config");
    config.value = { ...DEFAULTS, ...r.data };
  } catch {
    /* keep DEFAULTS — graceful degradation */
  }
}

export function useEventConfig() {
  return {
    config,
    loadEventConfig,
    pointsPerLottery: computed(() => config.value.points_per_lottery),
    unitLabel: computed(() => config.value.unit_label),
    maxUnitsPerQr: computed(() => config.value.max_units_per_qr),
    redemptionTiers: computed(() => config.value.redemption_tiers),
    lotteryTiers: computed(() => config.value.lottery_tiers),
    lotteryEnabled: computed(() => config.value.lottery_enabled),
    redemptionEnabled: computed(() => config.value.redemption_enabled),
    eventStartAt: computed(() => config.value.event_start_at),
    eventEndAt: computed(() => config.value.event_end_at),
    lineFriendUrl: computed(() => config.value.line_friend_url),
    isLive: computed(() => config.value.is_live),
    eventStatus: computed(() => config.value.status),
  };
}
